import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

/**
 * Server-side pagination, search and filtering for list pages.
 *
 * State lives in the URL, so an officer who opens a record and comes back
 * lands on the same page with the same search still applied.
 */
export default function usePaginatedList(endpoint, {
  pageSize = 25,
  filters = {},
  searchDebounceMs = 300,
} = {}) {
  const [params, setParams] = useSearchParams();

  const page = parseInt(params.get('page') || '1', 10);
  const search = params.get('q') || '';
  const activeFilter = params.get('filter') || '';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Local mirror so typing feels instant while the request is debounced.
  const [searchInput, setSearchInput] = useState(search);
  const debounce = useRef(null);
  const reqId = useRef(0);

  useEffect(() => { setSearchInput(search); }, [search]);

  const setParam = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError('');
    try {
      const query = { page, pageSize, ...(filters[activeFilter] || {}) };
      if (search) query.search = search;
      const { data } = await api.get(endpoint, { params: query });
      // Stale response from a superseded request — discard it.
      if (id !== reqId.current) return;
      setRows(data.data || []);
      setTotal(data.meta?.total ?? (data.data?.length || 0));
    } catch (e) {
      if (id !== reqId.current) return;
      setError(e.response?.data?.error?.message || 'Could not load this list.');
      setRows([]);
      setTotal(0);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, page, pageSize, search, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const onSearchChange = (value) => {
    setSearchInput(value);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setParam({ q: value, page: 1 }), searchDebounceMs);
  };

  const onFilterChange = (key) => setParam({ filter: key === activeFilter ? '' : key, page: 1 });
  const onPageChange = (p) => setParam({ page: p });

  return {
    rows, total, loading, error,
    page, pageSize, pageCount: Math.max(1, Math.ceil(total / pageSize)),
    search: searchInput, activeFilter,
    onSearchChange, onFilterChange, onPageChange,
    reload: load,
  };
}